import { useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAppSelector } from '../hooks/useAppDispatch'
import { useGetFollowedDiscussionsQuery, useUnfollowDiscussionMutation } from '../store/api/discussionsApi'
import { useGetFollowedUsersQuery, useUnfollowUserMutation, useGetPreferencesQuery, useUpdatePreferencesMutation } from '../store/api/usersApi'
import { contentPreview, timeAgoShort } from '../lib/avatar'
import { DISCUSSIONS_PAGE_SIZE, CATEGORY_BADGE_PREVIEW_MAX } from '../lib/config'
import Avatar from '../components/ui/Avatar'
import { useInfiniteScroll } from '../hooks/useInfiniteScroll'
import { Button, Card, EmptyState, Spinner, RefreshButton, SentinelLoader } from '../components/ui'
import s from '../styles/pages/FollowingPage.module.css'

type Tab = 'discussions' | 'users' | 'tags'

const SkeletonRow = () => (
  <div className={s.skeletonRow}>
    <div className={s.skeletonAvatar} />
    <div className={s.skeletonBody}>
      <div className={s.skeletonLine1} />
      <div className={s.skeletonLine2} />
    </div>
    <div className={s.skeletonAction} />
  </div>
)

export default function FollowingPage() {
  const { user } = useAppSelector((s) => s.auth)
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('discussions')
  const [discCursor, setDiscCursor] = useState<string | undefined>(undefined)
  const [userCursor, setUserCursor] = useState<string | undefined>(undefined)

  const { data: prefsData } = useGetPreferencesQuery(undefined, { skip: !user })
  const [updatePreferences] = useUpdatePreferencesMutation()
  const followedTags: string[] = (prefsData?.preferences as any)?.followedTags ?? []

  const handleUnfollowTag = async (slug: string) => {
    const updated = followedTags.filter((t) => t !== slug)
    await updatePreferences({ followedTags: updated } as any).catch(() => {})
  }

  const { data: discData, isLoading: discLoading, isFetching: discFetching, refetch: refetchDisc } = useGetFollowedDiscussionsQuery(
    { cursor: discCursor, limit: DISCUSSIONS_PAGE_SIZE },
    { skip: !user }
  )

  const { data: usersData, isLoading: usersLoading, isFetching: usersFetching, refetch: refetchUsers } = useGetFollowedUsersQuery(
    { cursor: userCursor, limit: DISCUSSIONS_PAGE_SIZE },
    { skip: !user }
  )

  const [unfollowDiscussion] = useUnfollowDiscussionMutation()
  const [unfollowUser] = useUnfollowUserMutation()

  const loadMoreDisc = useCallback(() => {
    if (discData?.hasMore && discData.nextCursor) setDiscCursor(discData.nextCursor)
  }, [discData])
  const loadMoreUsers = useCallback(() => {
    if (usersData?.hasMore && usersData.nextCursor) setUserCursor(usersData.nextCursor)
  }, [usersData])

  const discSentinelRef = useInfiniteScroll({ hasMore: discData?.hasMore ?? false, isFetching: discFetching, onLoadMore: loadMoreDisc })
  const userSentinelRef = useInfiniteScroll({ hasMore: usersData?.hasMore ?? false, isFetching: usersFetching, onLoadMore: loadMoreUsers })

  if (!user) { void navigate('/'); return null }

  const discussions = discData?.data ?? []
  const users = usersData?.data ?? []

  return (
    <>
      <Helmet>
        <title>Following — Ekkam AI</title>
      </Helmet>

      <div>
        <div className={s.pageHeader}>
          <button onClick={() => navigate(-1)} className={s.backBtn}>← Back</button>
          <span className={s.breadcrumb}>/</span>
          <h1 className={s.pageTitle}>Following</h1>
        </div>

        <div className={s.tabBar}>
          <div className={s.tabs}>
            {(['discussions', 'users', 'tags'] as Tab[]).map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`${s.tab} ${activeTab === tab ? s.active : ''}`}>
                {tab === 'discussions' ? '💬' : tab === 'users' ? '👤' : '🏷️'} {tab}
                {tab === 'discussions' && !discLoading && discussions.length > 0 && (
                  <span className={s.tabBadge}>{discussions.length}{discData?.hasMore ? '+' : ''}</span>
                )}
                {tab === 'users' && !usersLoading && users.length > 0 && (
                  <span className={s.tabBadge}>{users.length}{usersData?.hasMore ? '+' : ''}</span>
                )}
                {tab === 'tags' && followedTags.length > 0 && (
                  <span className={s.tabBadge}>{followedTags.length}</span>
                )}
              </button>
            ))}
          </div>
          <RefreshButton
            onClick={() => activeTab === 'discussions' ? void refetchDisc() : activeTab === 'users' ? void refetchUsers() : undefined}
            loading={activeTab === 'discussions' ? discFetching : activeTab === 'users' ? usersFetching : false}
            style={{
              cursor: activeTab === 'tags' ? 'default' : undefined,
              opacity: activeTab === 'tags' ? 0.3 : 1,
            }}
          />
        </div>

        {/* ── Discussions tab ── */}
        {activeTab === 'discussions' && (
          <Card style={{ overflow: 'hidden', padding: 0 }}>
            {discLoading && [1,2,3,4].map((i) => <SkeletonRow key={i} />)}

            {!discLoading && discussions.length === 0 && (
              <EmptyState
                icon="💬"
                title="Not following any discussions yet"
                description="Follow discussions to get notified when new replies arrive"
                action={<Link to="/" style={{ background: 'var(--accent-primary)', color: 'white', padding: '0.55rem 1.25rem', borderRadius: '8px', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600 }}>Browse discussions</Link>}
                style={{ border: 'none', borderRadius: 0 }}
              />
            )}

            {discussions.map((d, i) => (
              <div
                key={d.id}
                className={s.listRow}
                style={{ borderBottom: i < discussions.length - 1 ? '1px solid var(--border)' : 'none' }}
              >
                <Avatar name={(d as any).author?.profile?.displayName ?? 'User'} avatarUrl={(d as any).author?.profile?.avatarUrl} size={36} />
                <Link to={`/discussions/${d.id}`} className={s.discLink}>
                  <div className={s.discTitle}>{d.title}</div>
                  <div className={s.discMeta}>
                    {d.categories?.slice(0, CATEGORY_BADGE_PREVIEW_MAX).map((cat) => (
                      <span key={cat.id} style={{ fontSize: '0.7rem', fontWeight: 500, color: cat.color ?? 'var(--accent-primary)' }}>
                        {cat.icon} {cat.name}
                      </span>
                    ))}
                    <span className={s.discMetaTime}>· 💬 {d.commentCount} · {timeAgoShort(d.updatedAt ?? d.createdAt)}</span>
                  </div>
                </Link>
                <button onClick={() => void unfollowDiscussion(d.id)} className={s.unfollowBtn}>Unfollow</button>
              </div>
            ))}

              <SentinelLoader ref={discSentinelRef} isFetching={discFetching && discussions.length > 0} />
          </Card>
        )}

        {/* ── Tags tab ── */}
        {activeTab === 'tags' && (
          <Card style={{ overflow: 'hidden', padding: 0 }}>
            {followedTags.length === 0 ? (
              <EmptyState
                icon="🏷️"
                title="Not following any tags yet"
                description={<>Click <strong>+</strong> next to a trending tag in the sidebar to follow it</>}
                style={{ border: 'none', borderRadius: 0 }}
              />
            ) : (
              <div className={s.tagsGrid}>
                {followedTags.map((slug) => (
                  <div key={slug} className={s.tagPill}>
                    <span className={s.tagHash}>#</span>{slug}
                    <button onClick={() => void handleUnfollowTag(slug)} title={`Unfollow #${slug}`} className={s.tagRemoveBtn}>×</button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* ── Users tab ── */}
        {activeTab === 'users' && (
          <Card style={{ overflow: 'hidden', padding: 0 }}>
            {usersLoading && [1,2,3,4].map((i) => <SkeletonRow key={i} />)}

            {!usersLoading && users.length === 0 && (
              <EmptyState
                icon="👤"
                title="Not following anyone yet"
                description="Follow users to stay updated on their discussions"
                action={<Link to="/" style={{ background: 'var(--accent-primary)', color: 'white', padding: '0.55rem 1.25rem', borderRadius: '8px', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600 }}>Browse discussions</Link>}
                style={{ border: 'none', borderRadius: 0 }}
              />
            )}

            {users.map((u, i) => (
              <div key={u.id} className={s.listRow} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <Link to={`/profile/${u.id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0, textDecoration: 'none' }}>
                  <Avatar name={u.profile?.displayName ?? 'User'} avatarUrl={u.profile?.avatarUrl ?? null} size={38} />
                  <div className={s.userInfo}>
                    <div className={s.userName}>{u.profile?.displayName ?? 'User'}</div>
                    {u.profile?.bio && <div className={s.userBio}>{u.profile.bio}</div>}
                    {!u.profile?.bio && <div className={s.userJoined}>Joined {timeAgoShort(u.createdAt)}</div>}
                  </div>
                </Link>
                <button onClick={() => void unfollowUser(u.id)} className={s.unfollowBtn}>Unfollow</button>
              </div>
            ))}

            <SentinelLoader ref={userSentinelRef} isFetching={usersFetching && users.length > 0} />
          </Card>
        )}
      </div>
    </>
  )
}
